import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';

/**
 * The mobile header's search icon. Live has no search field in the mobile bar: the icon opens the
 * menu drawer, whose first row is the search field, and puts the cursor in it. This does the same,
 * so there is one mobile field rather than a second one behind a dialog.
 */
class EqHeaderSearchTrigger extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    // `toggle` does not bubble, so this listens in the capture phase, which reaches the element
    // whichever way the drawer was closed — this button, the burger, Escape or the close control.
    document.addEventListener('toggle', this.#syncExpanded, {
      capture: true,
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    super.disconnectedCallback();
  }

  /** @returns {HTMLInputElement | null} */
  get #input() {
    const id = this.dataset.targetInput;
    const input = id ? document.getElementById(id) : null;
    return input instanceof HTMLInputElement ? input : null;
  }

  /** @returns {HTMLButtonElement | null} */
  get #button() {
    return this.querySelector('button');
  }

  /**
   * Keeps the button's state in step with the drawer it opens.
   *
   * @param {Event} event
   */
  #syncExpanded = (event) => {
    const details = event.target;

    if (!(details instanceof HTMLDetailsElement)) return;

    const input = this.#input;

    if (!input || !details.contains(input)) return;

    const button = this.#button;

    if (!button) return;

    button.setAttribute('aria-expanded', String(details.open));

    if (details.id) button.setAttribute('aria-controls', details.id);
  };

  openDrawerSearch() {
    const input = this.#input;
    const drawer = /** @type {any} */ (input?.closest('header-drawer'));

    if (!input || !drawer) return;

    const focusField = () => input.focus();

    if (drawer.isOpen) {
      focusField();
      return;
    }

    // The summary's own click is what opens the `details` and runs Horizon's toggle, which owns the
    // animation and the focus trap. Calling `open()` here instead leaves the `details` shut, so the
    // panel keeps its closed transform and the field sits off-screen. `aria-expanded` follows from
    // the `toggle` that click fires.
    const summary = drawer.querySelector('summary');

    if (!summary) return;

    summary.click();

    // That trap lands on the drawer's first control once the panel settles, so take focus back.
    // `subtree: false` keeps this to the panel's own animation — an `animationend` listener would
    // also hear the accordions and promo cards inside it, and fire while the panel is still moving.
    // `open()` adds the opening class inside a rAF, so the animation only exists from the frame
    // after that; asked any sooner there is nothing to wait on and the callback runs immediately.
    const panel = drawer.querySelector('.menu-drawer') ?? drawer;

    requestAnimationFrame(() => requestAnimationFrame(() => onAnimationEnd(panel, focusField, { subtree: false })));
  }
}

if (!customElements.get('eq-header-search-trigger')) {
  customElements.define('eq-header-search-trigger', EqHeaderSearchTrigger);
}
